/**
 * `/today` — the Hermes digest (design §2.4 / §2.7).
 *
 * This screen replaced a shell that said, honestly, that the pipeline had not
 * shipped. It has now: every card here is a real `digest_items` row with its
 * evidence, its score breakdown and — when the guards let one through — its
 * draft.
 *
 * ## Three states, and none of them is a spinner that never resolves
 *
 *  - **No digest yet.** The first run has not happened. Says so, and offers to
 *    build one, rather than implying the feature is broken.
 *  - **A SKIPPED digest.** §2.4 step 0 records why (no connected accounts, a
 *    hard budget cap). Showing that reason is the whole reason it is stored: a
 *    missing 07:00 report that explains nothing is the worst version of this.
 *  - **An empty digest.** §2.7's own rule: "when there is genuinely nothing:
 *    show streak + upcoming meetings instead of manufacturing work." Nothing due
 *    is a real, good state.
 *
 * The `digest_ready` SSE frame invalidates this view, so a digest that finishes
 * while the tab is open appears without a refresh.
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  SkeletonRows,
} from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { PlugIcon, RefreshCwIcon, SunriseIcon } from 'lucide-react';
import {
  useCurrentEventsQuery,
  useDigestQuery,
  useRegenerateDigestMutation,
} from '../../api/queries.ts';
import { FollowUpCard } from '../../components/digest/follow-up-card.tsx';
import { PageBody, PageHeader } from '../../components/page.tsx';
import { ActionPrompt } from '../../components/placeholders.tsx';
import { FeatureBoundary, RouteErrorPanel } from '../../components/route-error-boundary.tsx';
import { formatDateTime } from '../../lib/format.ts';

export const Route = createFileRoute('/_authed/today')({
  component: TodayPage,
  errorComponent: RouteErrorPanel,
});

/** `YYYY-MM-DD` in the BROWSER's timezone — the same key the worker uses. */
function localToday(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function TodayPage() {
  const digest = useDigestQuery();
  const regenerate = useRegenerateDigestMutation();
  const current = useCurrentEventsQuery();
  const data = digest.data ?? null;

  const description =
    data === null
      ? 'Your daily digest.'
      : data.generatedAt === null
        ? `${data.localDate} · not generated yet`
        : `${data.localDate} · built ${formatDateTime(data.generatedAt) ?? ''}`;

  return (
    <>
      <PageHeader
        title="Today"
        description={description}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => regenerate.mutate(localToday())}
            disabled={regenerate.isPending}
          >
            <RefreshCwIcon className="size-3.5" aria-hidden />
            Rebuild
          </Button>
        }
      />
      <PageBody>
        {digest.isLoading ? (
          <SkeletonRows rows={3} />
        ) : data === null ? (
          <EmptyState
            icon={<SunriseIcon aria-hidden />}
            title="No digest yet"
            description="Hermes builds this list every morning from your email, meetings and promises. Nothing is being suppressed — the first run has not happened."
            action={
              <Button
                onClick={() => regenerate.mutate(localToday())}
                disabled={regenerate.isPending}
              >
                Build one now
              </Button>
            }
          />
        ) : data.status === 'skipped' && data.skipReason === 'no_connected_accounts' ? (
          // Bucket (b) of the placard sweep: the unblock is an ACTION, so render
          // the action. "No connected account" as an empty state left the reader
          // to work out that Settings → Connected accounts was the answer.
          <ActionPrompt
            icon={<PlugIcon aria-hidden />}
            title="Nothing to build a digest from yet"
            description="Hermes reads your calendar and mail to work out who needs a follow-up. Connect an account and tomorrow's digest will have something in it."
            actionLabel="Connect an account"
            to="/settings/accounts"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => regenerate.mutate(localToday())}
              disabled={regenerate.isPending}
            >
              Try again now
            </Button>
          </ActionPrompt>
        ) : data.status === 'skipped' ? (
          <EmptyState
            icon={<SunriseIcon aria-hidden />}
            title="Today's digest was skipped"
            description={
              // The stored reason, verbatim. A skipped run that explains nothing
              // is indistinguishable from a broken one.
              data.skipReason ?? 'The run was skipped and did not record a reason.'
            }
          />
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={<SunriseIcon aria-hidden />}
            title={data.headline ?? 'Nothing needs you today'}
            description={
              data.focusNote ??
              'No follow-ups are due. That is a real state, not an empty screen — the upcoming meetings below are what is next.'
            }
          />
        ) : (
          <>
            <Card>
              <CardHeader className="gap-1 pb-3">
                <CardTitle>{data.headline ?? 'Today'}</CardTitle>
                {data.focusNote === null ? null : (
                  <p className="text-sm text-muted-foreground">{data.focusNote}</p>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Badge variant="secondary">{data.itemCount} item(s)</Badge>
                  {data.degraded ? (
                    // §2.4 step 0's degraded mode, surfaced. The items are real;
                    // only the drafting was skipped, and the user is told which.
                    <Badge variant="outline">{data.degradedReason ?? 'Drafts were skipped'}</Badge>
                  ) : null}
                  {data.deliveredChannels.length === 0 ? null : (
                    <span className="text-xs text-muted-foreground">
                      Delivered via {data.deliveredChannels.join(', ')}
                    </span>
                  )}
                </div>
              </CardHeader>
            </Card>

            <div className="flex flex-col gap-3">
              {data.items.map((item) => (
                // One boundary per card: design §2.12's "one dead integration
                // may never take down the CRM", applied within a screen. A card
                // whose draft state confuses a renderer must not blank the other
                // four.
                <FeatureBoundary key={item.id} feature={`the card for ${item.contactName}`}>
                  <FollowUpCard item={item} />
                </FeatureBoundary>
              ))}
            </div>
          </>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Happening now</CardTitle>
          </CardHeader>
          <CardContent>
            {current.isLoading ? (
              <SkeletonRows rows={2} />
            ) : current.isError ? (
              <p className="text-sm text-muted-foreground">
                Could not read your calendar mirror. The rest of this page still works.
              </p>
            ) : (current.data?.candidates ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing on your calendar in the next few minutes.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {(current.data?.candidates ?? []).map((candidate) => (
                  <li key={candidate.event.id} className="flex items-baseline gap-2 text-sm">
                    <span className="font-medium">{candidate.event.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(candidate.event.startsAt) ?? 'no start time'} ·{' '}
                      {candidate.reason.replace('_', ' ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  );
}
